/**
 * @fileoverview Component Organism main to display and edit the customer details in the inbox.
 * Orchestrates the display and editing of data, tags and details accordion, with optimized and modular logic.
 * @module CustomerDetailsMC
 * @category Component - Organism
 * @since 1.0.0
 * @version 2.1.0
 */
import React, { memo, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setCustomerDetailsEditing } from "../../../actions/inbox/customerDataActions";
import {
  CustomerDetailsAccordionMC,
  CustomerDetailFormMC,
  CustomerDetailMC,
  CustomerDetailTagsMC
} from "./components";
import useCustomerDetailData from "../../hooks/inbox/data/useCustomerDetailData";
import { useTraceUpdate } from "../../hooks/shared/useDebugRenders";

/**
 * Component Organism main to display and edit the customer details in the inbox.
 *
 * Atomic Design: Organism
 * - Orchestrates CustomerDetailMC, CustomerDetailFormMC, CustomerDetailTagsMC and CustomerDetailsAccordionMC
 * - Allows switching between display and editing of customer data
 * - Uses TailwindCSS with .tw-wrapper
 * - Optimized with memo, useState and memoized callbacks
 */
const CustomerDetailsMC = memo(({ conversation, customerData, refreshCustomerData }) => {
  const dispatch = useDispatch();
  const [isEditingCustomerEnabled, setIsEditingCustomerEnabled] = useState(false);
  const {
    customerFields,
    customFields,
    loadCustomerFieldData
  } = useCustomerDetailData(customerData?.id);

  // Memoized handlers
  const handleEdit = React.useCallback(() => {
    loadCustomerFieldData?.();
    setIsEditingCustomerEnabled(true);
    dispatch(setCustomerDetailsEditing(true));
  }, [dispatch, loadCustomerFieldData]);

  // Callback to close the edit form
  const handleClose = React.useCallback(() => {
    setIsEditingCustomerEnabled(false);
    dispatch(setCustomerDetailsEditing(false));
  }, [dispatch]);

  useEffect(() => () => {
    dispatch(setCustomerDetailsEditing(false));
  }, [dispatch]);

  // 🚀 DEBUG RENDER - Track re-renders in CustomerDetailsMC (OPTIMIZED)
  useTraceUpdate('CustomerDetailsMC', {
    customerDataId: customerData?.id,
    hasCustomerData: !!customerData,
    isEditingCustomerEnabled,
    customerFields: customerFields?.length || 0,
    customFields: customFields?.length || 0,
    // Reduce debug information to avoid noise
    customerFieldsHash: customerFields?.length ? JSON.stringify(customerFields.map((f) => f.identifier || '')) : '[]',
    customFieldsHash: customFields?.length ? JSON.stringify(customFields.map((f) => f.identifier || '')) : '[]',
    customerHash: customerData ? JSON.stringify({
      id: customerData.id,
      first_name: customerData.first_name,
      last_name: customerData.last_name
    }) : null
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  return (
    <>
      {!isEditingCustomerEnabled && (
        <div className="tw-flex tw-flex-col tw-h-[calc(100vh-250px)] tw-max-h-screen tw-min-h-[400px]">
          <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-pr-1">
            <CustomerDetailMC
              customerData={customerData}
              onEdit={handleEdit}
              customerFields={customerFields}
              customFields={customFields}
              loadCustomerFieldData={loadCustomerFieldData}
            />
            <CustomerDetailTagsMC
              tags={customerData?.tags}
              customerId={customerData?.id}
              platform={customerData?.platform}
            />
            <CustomerDetailsAccordionMC
              conversation={conversation}
              customer={customerData}
            />
          </div>
        </div>
      )}
      {isEditingCustomerEnabled && (
        <CustomerDetailFormMC
          platform={customerData?.platform}
          customer={customerData}
          onClose={handleClose}
          customerFields={customerFields}
          customFields={customFields}
          refreshCustomerData={refreshCustomerData}
        />
      )}
    </>
  );
});

CustomerDetailsMC.displayName = 'CustomerDetailsMC';

export default CustomerDetailsMC;
